/**
 * SideBar — permission-gated navigation sidebar.
 *
 * Renders navigation groups based on the current user's permission set
 * (read from AuthContext). Each group is only visible if the user holds
 * at least one of the required permissions for that section.
 *
 * Behaviour:
 * - Expands when isSidebarOpen is true OR when the cursor enters the
 *   left-edge trigger zone (desktop hover-to-expand).
 * - On mobile, an overlay backdrop closes the sidebar on outside tap.
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './SideBar.css';

const SideBar = ({ isSidebarOpen, onClose }) => {
  const { hasPermission, hasAnyPermission } = useAuth();

  const [isHovered, setIsHovered] = useState(false);
  const [open, setOpen] = useState({
    strategy: false,
    planning: false,
    reports: false,
    structure: false,
    devplan: false,
    tenants: false,
  });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (e.clientX <= 20 && !isHovered) setIsHovered(true);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isHovered]);

  /** Navigate to path, skipping if already on that route. */
  const handleNavigation = (path) => {
    if (location.pathname === path) return;
    navigate(path);
  };

  /** Toggle a collapsible nav group open/closed. */
  const toggle = (key) =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const isExpanded = isSidebarOpen || isHovered;

  return (
    <>
      <div
        className="sidebar-trigger-zone"
        onMouseEnter={() => setIsHovered(true)}
      />

      {/* Mobile backdrop — closes sidebar on tap outside */}
      {isExpanded && (
        <div
          className="sidebar-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="sidebar-content">
          <ul className="sidebar-nav">

            {/* OVERVIEW */}
            {hasPermission('overview:view') && (
              <li className="nav-section">
                <span className="nav-heading">Overview</span>
                <li className="nav-item">
                  <button
                    onClick={() => handleNavigation('/dashboard')}
                    className="nav-link"
                    title="Overview"
                  >
                    <i className="bi bi-grid" />
                    <span className="nav-text">Overview</span>
                  </button>
                </li>
              </li>
            )}

            {/* TALENT GOALS */}
            {hasAnyPermission(['core:framework', 'functional:framework:own']) && (
              <li className="nav-section">
                <span className="nav-heading">Talent Goals</span>
                <li className="nav-item">
                  <button 
                    onClick={() => toggle('structure')} 
                    className="nav-link"
                    title="Organization Structure"
                  >
                    <i className="bi bi-building" />
                    <span className="nav-text">Organization Structure</span>
                    <i className={`bi bi-chevron-down chevron ${open.structure ? 'open' : ''}`} />
                  </button>

                  <ul className={`nav-content ${open.structure ? 'open' : ''}`}>
                    {hasPermission('core:framework') && (
                      <li className="nav-item">
                        <button
                          onClick={() => handleNavigation('/corporate-structure')}
                          className="nav-link sub-link"
                        >
                          <i className="bi bi-grid-3x3-gap"></i>
                          <span className="nav-text">Corporate Structure</span>
                        </button>
                      </li>
                    )}

                    {hasPermission('functional:framework:own') && (
                      <li className="nav-item">
                        <button
                          onClick={() => handleNavigation('/functional-structure')}
                          className="nav-link sub-link"
                        >
                          <i className="bi bi-diagram-3"></i>
                          <span className="nav-text">Functional Structure</span>
                        </button>
                      </li>
                    )}
                  </ul>
                </li>
              </li>
            )}

            {/* TALENT STRATEGY */}
            {hasPermission('strategy:corporate') && (
              <li className="nav-section">
                <span className="nav-heading">Talent Strategy</span>
                <li className="nav-item">
                  <button
                    onClick={() => toggle('strategy')}
                    className="nav-link"
                    title="Competency Model"
                  >
                    <i className="bi bi-bullseye" />
                    <span className="nav-text">Competency Model</span>
                    <i className={`bi bi-chevron-down chevron ${open.strategy ? 'open' : ''}`} />
                  </button>

                  <ul className={`nav-content ${open.strategy ? 'open' : ''}`}>
                    <li className="nav-item">
                      <button
                        onClick={() => handleNavigation('/competency-model')}
                        className="nav-link sub-link"
                      >
                        <i className="bi bi-layers"></i>
                        <span className="nav-text">Setup Model</span>
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        onClick={() => handleNavigation('/competency-framework')}
                        className="nav-link sub-link"
                      >
                        <i className="bi bi-eye"></i>
                        <span className="nav-text">View Model</span>
                      </button>
                    </li>
                  </ul>
                </li>
              </li>
            )}

            {/* TALENT PLANNING */}
            {hasPermission('planning:role_maps') && (
              <li className="nav-section">
                <span className="nav-heading">Talent Planning</span>
                <li className="nav-item">
                  <button
                    onClick={() => handleNavigation('/competency-mapping')}
                    className="nav-link"
                    title="Competency Mapping"
                  >
                    <i className="bi bi-person" />
                    <span className="nav-text">Competency Mapping</span>
                  </button>
                </li>
              </li>
            )}

            {/* TALENT REVIEW */}
            {hasPermission('review:evaluate') && (
              <li className="nav-section">
                <span className="nav-heading">Talent Review</span>
                <li className="nav-item">
                  <button
                    onClick={() => handleNavigation('/evaluation')}
                    className="nav-link"
                    title="Evaluate"
                  >
                    <i className="bi bi-clipboard-check" />
                    <span className="nav-text">Evaluate</span>
                  </button>
                </li>
              </li>
            )}

            {/* TALENT PERFORMANCE */}
            {hasAnyPermission(['reports:corporate', 'reports:functional', 'reports:individual']) && (
              <li className="nav-section">
                <span className="nav-heading">Talent Performance</span>
                <li className="nav-item">
                  <button
                    onClick={() => toggle('reports')}
                    className="nav-link"
                    title="Reports"
                  >
                    <i className="bi bi-bar-chart" />
                    <span className="nav-text">Reports</span>
                    <i className={`bi bi-chevron-down chevron ${open.reports ? 'open' : ''}`} />
                  </button>

                  <ul className={`nav-content ${open.reports ? 'open' : ''}`}>
                    {hasPermission('reports:corporate') && (
                      <li className="nav-item">
                        <button
                          onClick={() => handleNavigation('/corporate-performance')}
                          className="nav-link sub-link"
                        >
                          <i className="bi bi-grid-3x3-gap"></i>
                          <span className="nav-text">Corporate Performance</span>
                        </button>
                      </li>
                    )}
                    {hasPermission('reports:functional') && (
                      <li className="nav-item">
                        <button
                          onClick={() => handleNavigation('/functional-performance')}
                          className="nav-link sub-link"
                        >
                          <i className="bi bi-diagram-3"></i>
                          <span className="nav-text">Functional Performance</span>
                        </button>
                      </li>
                    )}
                    {hasPermission('reports:individual') && (
                      <li className="nav-item">
                        <button
                          onClick={() => handleNavigation('/individual-performance')}
                          className="nav-link sub-link"
                        >
                          <i className="bi bi-person"></i>
                          <span className="nav-text">Individual Performance</span>
                        </button>
                      </li>
                    )}
                  </ul>
                </li>
              </li>
            )}

            {/* TALENT DEVELOPMENT */}
            {hasAnyPermission(['plan:corporate', 'plan:functional', 'plan:individual']) && (
              <li className="nav-section">
                <span className="nav-heading">Talent Development</span>
                <li className="nav-item">
                  <button
                    onClick={() => toggle('devplan')}
                    className="nav-link"
                    title="Development Plan"
                  >
                    <i className="bi bi-mortarboard" />
                    <span className="nav-text">Development Plan</span>
                    <i className={`bi bi-chevron-down chevron ${open.devplan ? 'open' : ''}`} />
                  </button>

                  <ul className={`nav-content ${open.devplan ? 'open' : ''}`}>
                    {hasPermission('plan:corporate') && (
                      <li className="nav-item">
                        <button
                          onClick={() => handleNavigation('/corporate-talent-plan')}
                          className="nav-link sub-link"
                        >
                          <i className="bi bi-grid-3x3-gap" />
                          <span className="nav-text">Corporate Talent Plan</span>
                        </button>
                      </li>
                    )}

                    {hasPermission('plan:functional') && (
                      <li className="nav-item">
                        <button
                          onClick={() => handleNavigation('/functional-talent-plan')}
                          className="nav-link sub-link"
                        >
                          <i className="bi bi-diagram-3" />
                          <span className="nav-text">Functional Talent Plan</span>
                        </button>
                      </li>
                    )}

                    {hasPermission('plan:individual') && (
                      <li className="nav-item">
                        <button
                          onClick={() => handleNavigation('/individual-talent-plan')}
                          className="nav-link sub-link"
                        >
                          <i className="bi bi-person-lines-fill" />
                          <span className="nav-text">Individual Talent Plan</span>
                        </button>
                      </li>
                    )}
                  </ul>
                </li>
              </li>
            )}

            {/* TALENT RESOURCES */}
            {hasAnyPermission(['resources:competency_bank', 'references:view']) && (
              <li className="nav-section">
                <span className="nav-heading">Talent Resources</span>

                {hasPermission('resources:competency_bank') && (
                  <li className="nav-item">
                    <button
                      onClick={() => handleNavigation('/competency-bank')}
                      className="nav-link"
                      title="Competency Bank"
                    >
                      <i className="bi bi-collection" />
                      <span className="nav-text">Competency Bank</span>
                    </button>
                  </li>
                )}

                {hasPermission('references:view') && (
                  <li className="nav-item">
                    <button
                      onClick={() => handleNavigation('/references')}
                      className="nav-link"
                      title="References"
                    >
                      <i className="bi bi-journal-text" />
                      <span className="nav-text">References</span>
                    </button>
                  </li>
                )}
              </li>
            )}

            {/* PLATFORM ADMIN */}
            {hasAnyPermission(['users:list', 'admin:tenants', 'admin:billing', 'admin:usage', 'admin:support', 'admin:org_health', 'admin:audit']) && (
              <li className="nav-section">
                <span className="nav-heading">Platform Admin</span>

                {hasPermission('users:list') && (
                  <li className="nav-item">
                    <button
                      onClick={() => handleNavigation('/user-management')}
                      className="nav-link"
                      title="User Management"
                    >
                      <i className="bi bi-people-fill" />
                      <span className="nav-text">User Management</span>
                    </button>
                  </li>
                )}

                {hasPermission('admin:tenants') && (
                  <li className="nav-item">
                    <button
                      onClick={() => handleNavigation('/admin/tenants')}
                      className="nav-link"
                      title="Tenant Management"
                    >
                      <i className="bi bi-buildings" />
                      <span className="nav-text">Tenant Orgs</span>
                    </button>
                  </li>
                )}

                {hasPermission('admin:billing') && (
                  <li className="nav-item">
                    <button
                      onClick={() => handleNavigation('/admin/billing')}
                      className="nav-link"
                      title="Subscription and Billing"
                    >
                      <i className="bi bi-credit-card" />
                      <span className="nav-text">Billing</span>
                    </button>
                  </li>
                )}

                {hasPermission('admin:usage') && (
                  <li className="nav-item">
                    <button
                      onClick={() => handleNavigation('/admin/usage')}
                      className="nav-link"
                      title="Usage Analytics"
                    >
                      <i className="bi bi-graph-up" />
                      <span className="nav-text">Usage Analytics</span>
                    </button>
                  </li>
                )}

                {hasPermission('admin:support') && (
                  <li className="nav-item">
                    <button
                      onClick={() => handleNavigation('/admin/support')}
                      className="nav-link"
                      title="Support Actions"
                    >
                      <i className="bi bi-headset" />
                      <span className="nav-text">Support Actions</span>
                    </button>
                  </li>
                )}

                {hasPermission('admin:org_health') && (
                  <li className="nav-item">
                    <button
                      onClick={() => handleNavigation('/admin/org-health')}
                      className="nav-link"
                      title="Org Health"
                    >
                      <i className="bi bi-activity" />
                      <span className="nav-text">Org Health</span>
                    </button>
                  </li>
                )}

                {hasPermission('admin:audit') && (
                  <li className="nav-item">
                    <button
                      onClick={() => handleNavigation('/admin/audit')}
                      className="nav-link"
                      title="Audit Trails"
                    >
                      <i className="bi bi-journal-text" />
                      <span className="nav-text">Audit Trails</span>
                    </button>
                  </li>
                )}
              </li>
            )}

          </ul>
        </div>
      </aside>
    </>
  );
};

SideBar.propTypes = {
  isSidebarOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SideBar;